import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#formatFaqAccordions();
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Automatically formats question-and-answer content into interactive accordions
   */
  #formatFaqAccordions() {
    const path = window.location.pathname.toLowerCase();
    const isFaq =
      this.hasAttribute('data-auto-accordion') ||
      path.includes('/pages/faq') ||
      path.endsWith('/faq') ||
      path.endsWith('/faq/') ||
      (document.title && document.title.toLowerCase().includes('faq'));

    if (!isFaq) return;
    if (this.querySelector('details.collection-faq__item, details.faq-page__item')) return;

    const children = Array.from(this.children);
    if (children.length === 0) return;

    /**
     * Checks if an element is a question heading
     * @param {Element} el
     * @returns {boolean}
     */
    const isQuestion = (el) => {
      const tag = el.tagName.toLowerCase();
      if (['h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) {
        return true;
      }
      if (tag === 'p') {
        const strong = el.querySelector('strong, b');
        if (strong) {
          const pText = (el.textContent || '').trim();
          const strongText = (strong.textContent || '').trim();
          if (
            strongText.length >= 3 &&
            (pText === strongText ||
              pText.endsWith('?') ||
              pText.startsWith('Q:') ||
              pText.startsWith('Q.') ||
              /^\d+[\.\)]/.test(pText) ||
              Math.abs(pText.length - strongText.length) <= 6)
          ) {
            return true;
          }
        }
        const text = (el.textContent || '').trim();
        if (text.endsWith('?') && text.length < 180 && !text.includes('\n')) {
          return true;
        }
      }
      return false;
    };

    const questionIndices = [];
    children.forEach((el, i) => {
      if (isQuestion(el)) {
        questionIndices.push(i);
      }
    });

    if (questionIndices.length === 0) return;

    const fragment = document.createDocumentFragment();

    // Preserve any introductory elements before the first question
    const firstQIdx = questionIndices[0];
    if (firstQIdx !== undefined && firstQIdx > 0) {
      const introWrap = document.createElement('div');
      introWrap.classList.add('faq-page__intro-content', 'rte');
      for (let i = 0; i < firstQIdx; i++) {
        const child = children[i];
        if (child) introWrap.appendChild(child.cloneNode(true));
      }
      fragment.appendChild(introWrap);
    }

    const faqContainer = document.createElement('div');
    faqContainer.classList.add('collection-faq__items', 'faq-auto-accordion');

    questionIndices.forEach((qIdx, index) => {
      const qEl = children[qIdx];
      if (!qEl) return;

      const nextQIdx = index < questionIndices.length - 1 ? (questionIndices[index + 1] ?? children.length) : children.length;

      const details = document.createElement('details');
      details.classList.add('collection-faq__item');
      if (index === 0) {
        details.open = true;
      }

      const summary = document.createElement('summary');
      const spanText = document.createElement('span');

      // Extract question text cleanly
      const strong = qEl.querySelector('strong, b');
      if (strong) {
        spanText.innerHTML = strong.innerHTML;
      } else {
        spanText.innerHTML = qEl.innerHTML.replace(/^<p>|<\/p>$/gi, '').trim();
      }

      const icon = document.createElement('span');
      icon.className = 'collection-faq__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<i></i><i></i>';

      summary.appendChild(spanText);
      summary.appendChild(icon);
      details.appendChild(summary);

      const answerWrap = document.createElement('div');
      answerWrap.classList.add('collection-faq__answer', 'rte');

      for (let i = qIdx + 1; i < nextQIdx; i++) {
        const child = children[i];
        if (child) {
          answerWrap.appendChild(child.cloneNode(true));
        }
      }

      details.appendChild(answerWrap);
      faqContainer.appendChild(details);
    });

    fragment.appendChild(faqContainer);

    this.innerHTML = '';
    this.appendChild(fragment);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
